import React from "react"
import Img from "gatsby-image"
import { useIntl } from "hooks"
import { useStaticQuery, graphql } from "gatsby"
import { Bullet } from "components"

export default function OurSolutions() {
  const { t } = useIntl()
  const data = useStaticQuery(graphql`
    fragment FileOurSolutions on File {
      id
      childImageSharp {
        fluid(maxWidth: 1100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    query {
      measureBeyond: file(relativePath: { eq: "Solutions/measure-beyond.png" }) {
        ...FileOurSolutions
      }
      socialListening: file(relativePath: { eq: "Solutions/social-listening.png" }) {
        ...FileOurSolutions
      }
      runContentDistro: file(relativePath: { eq: "Solutions/run-content-distro.png" }) {
        ...FileOurSolutions
      }
      collectUserContent: file(relativePath: { eq: "Solutions/user-content.png" }) {
        ...FileOurSolutions
      }
    }
  `)

  return (
    <div className="container">
      <div className="flex flex-col items-center">
        <div className="-mt-1/12 mb-7.5 laptop:mb-13 w-full flex flex-col laptop:flex-row justify-between">
          {/* Third WhatIsSocialie Section with Image */}
          <div className="py-3.5 laptop:py-0 w-full laptop:w-7/12 laptop:-ml-1/12 tablet:w-90 tablet:mx-auto">
            <Img fluid={data.runContentDistro.childImageSharp.fluid} alt="run content distribution" />
          </div>
          <Frame
            containerClassNames="pt-0 laptop:pt-16.5 w-full laptop:w-5/12"
            heading={t("Solutions__SolutionThree--Heading")}
          >
            <div className="mb-5 space-y-3">
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionThree--ListItemOne")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionThree--ListItemTwo")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">
                  {t("Solutions__SolutionThree--ListItemThree")}
                </p>
              </div>
            </div>
          </Frame>
        </div>

        <div className="mb-7.5 laptop:mb-15 w-full flex flex-col-reverse laptop:flex-row justify-between">
          {/* Fourth WhatIsSocialie Section with Image */}
          <Frame
            containerClassNames="pt-0 laptop:pt-5.5 w-full laptop:w-5/12 flex items-center"
            heading={t("Solutions__SolutionFour--Heading")}
          >
            <div className="mb-5 space-y-3">
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionFour--ListItemOne")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionFour--ListItemTwo")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionFour--ListItemThree")}</p>
              </div>
            </div>
          </Frame>
          <div className="py-3.5 laptop:py-0 w-full laptop:w-7/12 laptop:-mr-1/12 tablet:w-90 tablet:mx-auto">
            <Img fluid={data.collectUserContent.childImageSharp.fluid} alt="collect user content" />
          </div>
        </div>
        <div className="mb-7.5 laptop:mb-15 w-full flex flex-col laptop:flex-row justify-between">
          <div className="py-3.5 laptop:py-0 w-full laptop:w-110% laptop:-ml-1/12 pr-1/12 phablet:max-h-87.5 phablet:w-auto tablet:min-h-87.5 tablet:mx-auto">
            <Img fluid={data.measureBeyond.childImageSharp.fluid} alt="measure social media" />
          </div>
          <Frame
            containerClassNames="pt-0 laptop:pt-5.5 w-full laptop:w-5/12"
            heading={t("Solutions__SolutionOne--Heading")}
          >
            <div className="mb-5 space-y-3">
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionOne--ListItemOne")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionOne--ListItemTwo")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionOne--ListItemThree")}</p>
              </div>
            </div>
          </Frame>
        </div>

        <div className="mb-7.5 laptop:mb-9 w-full flex flex-col-reverse laptop:flex-row justify-between">
          {/* Second WhatIsSocialie Section with Image */}
          <Frame
            containerClassNames="pt-3.5 laptop:pt-18.5 w-full laptop:w-5/12"
            heading={t("Solutions__SolutionTwo--Heading")}
          >
            <div className="mb-5 space-y-3">
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionTwo--ListItemOne")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionTwo--ListItemTwo")}</p>
              </div>
              <div className="flex">
                <Bullet />
                <p className="text-body text-p tracking-10 leading-h6">{t("Solutions__SolutionTwo--ListItemThree")}</p>
              </div>
            </div>
          </Frame>
          <div className="w-full laptop:w-7/12 laptop:-mr-1/12 tablet:w-90 tablet:mx-auto">
            <Img fluid={data.socialListening.childImageSharp.fluid} alt="listening social" />
          </div>
        </div>
      </div>
    </div>
  )
}

interface FrameProps {
  heading: string
  children: React.ReactNode
  containerClassNames: string
}

function Frame(props: FrameProps) {
  const { heading, children, containerClassNames } = props
  return (
    <div className={containerClassNames}>
      <div className="mb-6 flex justify-center">
        <div>
          <div className="mb-2.5">
            <h3 className="font-serif text-h4 leading-h4 text-body tracking-11">{heading}</h3>
          </div>
          <div className="mb-4">{children}</div>
        </div>
      </div>
    </div>
  )
}
